import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import Collapse from '@mui/material/Collapse';
import { getPolicyStreamFormLabel } from '../../../../../PolicyStream/PolicyStreamReviewPage/utility';
import { useAllowByPermission } from '../../../../../../hooks';
import { useAuth } from '@calefy-inc/authentication';
import { useMutation } from '@apollo/client';
import { DisplayInlineError } from '../../../../../common/ErrorHandling';
import { graphql } from '../../../../../../gql';
import type { BackendCompletedForm } from '../../../../../../Typescript/backend/classes';

const DELETE_COMPLETED_FORM = graphql(`
  mutation DeleteCompletedForm($token: String!, $formId: ID!) {
    deleteCompletedForm(token: $token, formId: $formId) {
      id
    }
  }
`);

//------------------------------------------------------------------------------------
interface DeleteCompletedFormButtonProps {
  form: BackendCompletedForm;
}

export function DeleteCompletedFormButton({
  form,
}: DeleteCompletedFormButtonProps) {
  const isAdmin = useAllowByPermission('is:admin');
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  if (!isAdmin) {
    return null;
  }

  return (
    <>
      <IconButton
        size='small'
        onClick={() => setDialogOpen(true)}
        color='error'
        aria-label='delete completed form'
      >
        <DeleteIcon />
      </IconButton>
      <DeleteCompletedFormDialog
        open={dialogOpen}
        form={form}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
}

//------------------------------------------------------------------------------------
interface DeleteCompletedFormDialogProps {
  open: boolean;
  form: BackendCompletedForm;
  onClose: () => void;
}

function DeleteCompletedFormDialog({
  open,
  form,
  onClose,
}: DeleteCompletedFormDialogProps) {
  const [enteredValue, setEnteredValue] = useState<string>('');
  const [formName, setFormName] = useState<string>(
    form.internalName ? getPolicyStreamFormLabel(form) : form.label(),
  );
  const { token } = useAuth();
  const [deleteCompletedForm, { loading, error, data }] = useMutation(
    DELETE_COMPLETED_FORM,
    {
      refetchQueries: ['Quote'],
    },
  );

  // update the form name if the form changes
  useEffect(() => {
    setFormName(
      form.internalName ? getPolicyStreamFormLabel(form) : form.label(),
    );
  }, [form]);

  // close everything once deleted
  useEffect(() => {
    if (data?.deleteCompletedForm?.id) {
      setEnteredValue('');
      onClose();
    }
  }, [data, onClose]);

  return (
    <Dialog
      open={open}
      data-testid='delete-completed-form-modal'
      disableEscapeKeyDown={loading}
      onClose={(_event, reason) => {
        if (loading) {
          return;
        }
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          setEnteredValue('');
          onClose();
        }
      }}
    >
      <DialogTitle>Are You Sure?</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          To be sure that this is really the form that you want to delete,
          please type the name of the form:
        </Typography>
        <Typography gutterBottom variant='subtitle1'>
          {formName}
        </Typography>
        <Collapse in={loading}>
          <Typography sx={{ mt: 1 }}>Deleting...</Typography>
        </Collapse>
        <DisplayInlineError error={error}>
          Error deleting completed form
        </DisplayInlineError>
        <TextField
          label='Form to Delete'
          id='form-to-delete'
          fullWidth
          value={enteredValue}
          disabled={loading}
          onChange={(e) => {
            setEnteredValue(e.target.value);
          }}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          disabled={loading}
          onClick={() => {
            setEnteredValue('');
            onClose();
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          onClick={() => {
            if (!token || !form?.id || enteredValue !== formName) {
              return;
            }
            deleteCompletedForm({
              variables: {
                token,
                formId: form.id,
              },
            });
          }}
          disabled={!token || !form?.id || enteredValue !== formName || !!error}
          color='error'
          variant='contained'
        >
          Delete
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
